import NodeStatus from 'features/Integrations/components/NodeStatus/NodeStatus';
import NodeStatusIcon from 'features/Integrations/features/ConnectedDevices/features/DeviceList/components/NodeStatusIcon/NodeStatusIcon';
import BaseTable from 'components/Tables/BaseTable';
import { usePerms } from 'contexts/PermsContext';
import GroupDualStatus from 'features/Integrations/components/GroupDualStatus/GroupDualStatus';
import { DeviceRow } from 'features/Integrations/features/ConnectedDeviceGroups/components/DeviceRow/DeviceRow';
import { hasDevicePermissionFromDevice } from 'features/Integrations/features/ConnectedSystems/utils/devicePermissions.utils';
import { selectVisibleDeviceTypes } from 'features/Integrations/state/selectors/integrationSelectors';
import {
  openDeviceSettings,
  openStageSettings,
  setLinkingGroupId,
} from 'features/Integrations/state/slices/deviceSlice';
import { setSelectedDevice } from 'features/Integrations/state/thunks';
import { DeviceIsSystemType } from 'features/Integrations/utils/deviceComparison.utils';
import { groupSearchFilter } from 'features/Integrations/utils/integrationSearch.utils';
import { useMemo, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { FaCog, FaExpandAlt } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { deviceImageList, getDeviceIconForGroup } from 'utils/devices/icon.utils';
import '../../../../../ConnectedDeviceGroups/assets/connectedDeviceGroups.css';
import GroupDeviceSettingsModal from '../../../../../ConnectedDeviceGroups/features/GroupSettingsModal';

export default function DeviceGroupList({ onOpenDevice, widget = false }) {
  const dispatch = useDispatch();
  const perms = usePerms();
  const isWidget = widget === true;
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const {
    data: deviceGroups = [],
    isLoading: deviceGroupsLoading,
    isError: deviceGroupsError,
  } = useGetGroupsQuery();
  // Tracks which group is open in the settings modal.
  const [settingsGroupId, setSettingsGroupId] = useState(null);
  const visibleDeviceTypes = useSelector(selectVisibleDeviceTypes);
  const [params] = useSearchParams();

  // Determine group type based on the group's type field (not individual devices)
  const getGroupType = (group) => {
    // Use the group's type field directly
    if (
      group.type === 'Technology Bridge_Flashing Lights' ||
      group.type === 'Manual Road Closure'
    ) {
      return 'Road Closures';
    } else if (group.type === 'Technology Bridge_Siren/Giant Voice') {
      return 'Sirens';
    }
    // If group type doesn't match our filter categories, return null (show anyway)
    return null;
  };

  // Check if group should be visible based on filters
  const isGroupVisible = (group) => {
    const effectiveVisibleDeviceTypes = visibleDeviceTypes || visibleDeviceTypes;
    if (!effectiveVisibleDeviceTypes) return true;

    // Get the group type (Road Closures, Sirens, etc.)
    const groupType = getGroupType(group);

    // If group has no recognized type, show it (mixed groups, empty groups, etc.)
    if (!groupType) return true;

    // Check if the group type is enabled in the filter
    return effectiveVisibleDeviceTypes[groupType] !== false;
  };

  // Filter groups based on visibility
  const filteredGroups = deviceGroups?.filter(isGroupVisible)?.filter(
    widget
      ? (group) => {
          const search = params.get('search');
          if (!search) return true;
          const searchTerm = search.replace(/\s+/g, '').toLowerCase();
          return (
            group.name?.toLowerCase().includes(searchTerm) ||
            String(group.id).toLowerCase().includes(searchTerm)
          );
        }
      : (group) => groupSearchFilter(group, connectedDevices),
  );

  const settingsGroup = useMemo(
    () => deviceGroups?.find((group) => group.id === settingsGroupId) || null,
    [deviceGroups, settingsGroupId],
  );

  const onOpenGroupSettingsModal = (groupId) => {
    if (isWidget) return;
    setSettingsGroupId(groupId);
  };

  const onCloseGroupSettingsModal = () => setSettingsGroupId(null);

  const renderGroupDevices = (group, deviceIds, isEmpty, compact = false) => {
    if (isEmpty) return <div>No devices in this group</div>;
    if (compact) {
      return (
        <div className="connected-device-groups-detail d-flex flex-column gap-2 bg-primary p-2 rounded-2">
          {deviceIds.map((deviceId, index) => {
            const device = connectedDevices.find((item) => item.id === deviceId);
            if (!device) return null;
            const canEdit = hasDevicePermissionFromDevice(perms, device, 'createEdit');
            return (
              <div
                key={`${group.id}-${deviceId}-${index}`}
                className={`d-flex align-items-center justify-content-between gap-2 py-2 ${
                  index === deviceIds.length - 1 ? '' : 'border-bottom border-secondary'
                }`}
                onClick={() => onOpenDevice(deviceId)}
              >
                <div className="d-flex align-items-center gap-2">
                  <img
                    src={deviceImageList[device?.device_type] || deviceImageList['RESCALERT']}
                    alt={device?.device_type || 'Rescalert'}
                    width={'22px'}
                  />
                  <div className="d-flex flex-column">
                    <span className="text-break">{device.alias}</span>
                    <span className="text-secondary small">ID: {device.node_id}</span>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <NodeStatusIcon device={device} />
                  {canEdit && (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-white"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (DeviceIsSystemType(device, 'Flood')) {
                          dispatch(openStageSettings(device.id));
                        } else {
                          dispatch(openDeviceSettings(device.id));
                        }
                      }}
                    >
                      <FaCog size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    return (
      <div className="connected-device-groups-detail w-100 px-3 py-2">
        <div className="row px-2 py-1 border-bottom border-secondary fw-bold align-items-center">
          <div className="col-12 col-md-4 col-lg-5">Device</div>
          <div className="col-12 col-md-3 col-lg-2">Available Licenses</div>
          <div className="col-12 col-md-3 col-lg-3 d-flex align-items-center">Status</div>
          <div className="col-12 col-md-2 col-lg-1" />
        </div>
        {deviceIds.map((deviceId, index) => (
          <div
            key={`${group.id}-${deviceId}-${index}`}
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => onOpenDevice(deviceId)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click();
            }}
          >
            <DeviceRow deviceId={deviceId} last={index === deviceIds.length - 1} hideDate />
          </div>
        ))}
      </div>
    );
  };

  const dropdownMenu = (group) => {
    const deviceIds = Array.isArray(group?.devices) ? group.devices : [];
    return renderGroupDevices(group, deviceIds, deviceIds.length === 0);
  };

  const groupRows =
    filteredGroups?.map((group) => ({
      ...group,
      devicesCount: group.devices?.length ?? 0,
      groupStatus: group,
    })) || [];

  const groupColumns = [
    {
      accessorKey: 'name',
      header: 'Name',
      Cell: ({ row }) => {
        const group = row.original;
        return (
          <div className="d-flex align-items-center gap-2">
            <img
              className="d-none d-md-block"
              src={getDeviceIconForGroup(group, connectedDevices)}
              alt={group?.type || 'RescAlert'}
              width={'24px'}
            />
            <span
              role={isWidget ? 'text' : 'button'}
              tabIndex={isWidget ? -1 : 0}
              className={isWidget ? 'text-break' : 'text-break text-decoration-underline'}
              onClick={(event) => {
                if (isWidget) return;
                event.stopPropagation();
                onOpenGroupSettingsModal(group.id);
              }}
              onKeyDown={(event) => {
                if (isWidget) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.stopPropagation();
                  onOpenGroupSettingsModal(group.id);
                }
              }}
            >
              {group.name}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'devicesCount',
      header: 'Devices',
      size: 20,
      minSize: 15,
      maxSize: 40,
      grow: false,
      Cell: ({ cell }) => <span>{cell.getValue()}</span>,
    },
    {
      id: 'Status',
      accessorKey: 'groupStatus',
      header: <span className="d-flex ms-5 ps-5">Status</span>,
      size: 140,
      minSize: 120,
      maxSize: 200,
      grow: false,
      Cell: ({ cell }) => (
        <div className="align-items-start justify-content-start">
          <GroupDualStatus group={cell.row.original} />
        </div>
      ),
    },
    {
      id: 'Settings',
      header: '',
      size: 60,
      minSize: 48,
      maxSize: 80,
      grow: false,
      Cell: ({ row }) => {
        if (isWidget) return null;
        return (
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-link p-0 text-white"
              onClick={(event) => {
                event.stopPropagation();
                onOpenGroupSettingsModal(row.original.id);
              }}
            >
              <FaCog size={18} />
            </button>
          </div>
        );
      },
    },
    ...(isWidget
      ? [
          {
            id: 'expand',
            header: '',
            size: 44,
            minSize: 36,
            maxSize: 60,
            grow: false,
            Cell: ({ row }) => (
              <div className="d-flex justify-content-end">
                <FaExpandAlt
                  size={18}
                  style={{ cursor: 'pointer' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    window.open(`/system/devices?tab=Connected+Device+Groups`);
                  }}
                />
              </div>
            ),
          },
        ]
      : []),
  ];

  // Link a selected group to the Map + Graph. Disabled in widget mode.
  const setLinkGroup = (group) => {
    if (isWidget) return;
    dispatch(setLinkingGroupId(group.id));
  };

  return (
    <>
      <div className="d-none d-xxl-block">
        <BaseTable
          columns={groupColumns}
          rows={groupRows}
          expandable
          onRowClick={setLinkGroup}
          enableRowVirtualization={groupRows.length > 50}
          dropdownMenu={dropdownMenu}
        />
      </div>

      <Accordion
        alwaysOpen
        defaultActiveKey={filteredGroups?.map((group) => String(group.id)) || []}
        className="d-block d-xxl-none bordered-table-accordion"
      >
        {filteredGroups?.map((group, index) => {
          const deviceIds = Array.isArray(group?.devices) ? group.devices : [];
          const isEmpty = deviceIds.length === 0;

          return (
            <Accordion.Item
              className="bordered-table-accordion-item"
              id={group.id}
              eventKey={String(group.id)}
              key={group.id}
            >
              <Accordion.Header>
                <div
                  className="d-flex w-100 justify-content-between align-items-center gap-2 pe-2"
                  onClick={() => setLinkGroup(group)}
                >
                  <div className="d-flex align-items-center gap-2">
                    <img
                      src={getDeviceIconForGroup(group, connectedDevices)}
                      alt={group?.type || 'RescAlert'}
                      width={'24px'}
                      height={'24px'}
                    />
                    <div className="d-flex flex-column">
                      <span
                        role={isWidget ? 'text' : 'button'}
                        tabIndex={isWidget ? -1 : 0}
                        className={isWidget ? 'text-break' : 'text-break text-decoration-underline'}
                        onClick={(event) => {
                          if (isWidget) return;
                          event.stopPropagation();
                          onOpenGroupSettingsModal(group.id);
                        }}
                        onKeyDown={(event) => {
                          if (isWidget) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.stopPropagation();
                            onOpenGroupSettingsModal(group.id);
                          }
                        }}
                      >
                        {group.name}
                      </span>
                      <span className="text-secondary">ID: {group.id}</span>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {!isEmpty && <GroupDualStatus group={group} />}
                    {!isWidget && (
                      <span
                        role="button"
                        tabIndex={0}
                        className="btn btn-link p-0 text-white"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenGroupSettingsModal(group.id);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.stopPropagation();
                            onOpenGroupSettingsModal(group.id);
                          }
                        }}
                      >
                        <FaCog size={18} />
                      </span>
                    )}
                    {isWidget && (
                      <span
                        role="button"
                        tabIndex={0}
                        className="text-white"
                        onClick={(event) => {
                          event.stopPropagation();
                          window.open(`/system/devices?tab=Connected+Device+Groups`);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.stopPropagation();
                            window.open(`/system/devices?tab=Connected+Device+Groups`);
                          }
                        }}
                      >
                        <FaExpandAlt size={18} />
                      </span>
                    )}
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className="bg-primary">
                {renderGroupDevices(group, deviceIds, isEmpty, true)}
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>
      <GroupDeviceSettingsModal
        show={!!settingsGroup}
        onClose={onCloseGroupSettingsModal}
        group={settingsGroup}
      />
    </>
  );
}
